export interface MergeTagContext {
  client_name?: string;
  client_email?: string;
  company_name?: string;
  agency_name?: string;
  agency_logo?: string;
  today?: string;
  valid_until?: string;
  // Sender fields
  sender_first_name?: string;
  sender_last_name?: string;
  sender_company?: string;
  sender_email?: string;
  sender_phone?: string;
  // Client fields
  client_first_name?: string;
  client_last_name?: string;
  client_company?: string;
  client_phone?: string;
}

export interface MergeTagDef {
  tag: string;       // the shortcode e.g. [Sender.FirstName]
  label: string;     // human-readable label
  category: string;
}

export const MERGE_TAG_DEFS: MergeTagDef[] = [
  // Sender
  { tag: '[Sender.FirstName]', label: 'Sender First Name', category: 'Sender' },
  { tag: '[Sender.LastName]', label: 'Sender Last Name', category: 'Sender' },
  { tag: '[Sender.Company]', label: 'Sender Company', category: 'Sender' },
  { tag: '[Sender.Email]', label: 'Sender Email', category: 'Sender' },
  { tag: '[Sender.Phone]', label: 'Sender Phone', category: 'Sender' },
  // Client
  { tag: '[Client.FirstName]', label: 'Client First Name', category: 'Client' },
  { tag: '[Client.LastName]', label: 'Client Last Name', category: 'Client' },
  { tag: '[Client.Company]', label: 'Client Company', category: 'Client' },
  { tag: '[Client.Email]', label: 'Client Email', category: 'Client' },
  { tag: '[Client.Phone]', label: 'Client Phone', category: 'Client' },
  // Proposal
  { tag: '[Proposal.Title]', label: 'Proposal Title', category: 'Proposal' },
  { tag: '[Proposal.Date]', label: 'Today\'s Date', category: 'Proposal' },
  { tag: '[Proposal.ValidUntil]', label: 'Valid Until Date', category: 'Proposal' },
];

export const MERGE_TAG_KEYS = [
  'client_name',
  'client_email',
  'company_name',
  'agency_name',
  'today',
  'valid_until',
] as const;

const TAG_RESOLVER: Record<string, keyof MergeTagContext> = {
  'Sender.FirstName': 'sender_first_name',
  'Sender.LastName': 'sender_last_name',
  'Sender.Company': 'sender_company',
  'Sender.Email': 'sender_email',
  'Sender.Phone': 'sender_phone',
  'Client.FirstName': 'client_first_name',
  'Client.LastName': 'client_last_name',
  'Client.Company': 'client_company',
  'Client.Email': 'client_email',
  'Client.Phone': 'client_phone',
  'Proposal.Title': 'agency_name',
  'Proposal.Date': 'today',
  'Proposal.ValidUntil': 'valid_until',
  // Legacy
  'client_name': 'client_name',
  'client_email': 'client_email',
  'company_name': 'company_name',
  'agency_name': 'agency_name',
  'today': 'today',
  'valid_until': 'valid_until',
};

export function resolveMergeTagsInText(text: string, ctx: MergeTagContext): string {
  // Resolve [Bracket.Style] tags
  let result = text.replace(/\[([A-Za-z]+\.[A-Za-z]+)\]/g, (match, key) => {
    const ctxKey = TAG_RESOLVER[key];
    if (ctxKey) {
      const value = ctx[ctxKey];
      return value !== undefined && value !== null && value !== '' ? String(value) : match;
    }
    return match;
  });
  // Resolve legacy {{double_brace}} tags
  result = result.replace(/\{\{\s*(\w+)\s*\}\}/g, (_, key) => {
    const value = (ctx as any)[key];
    return value !== undefined && value !== null && value !== '' ? String(value) : `{{${key}}}`;
  });
  return result;
}

export function resolveMergeTagsInBlocks(blocks: any[], ctx: MergeTagContext): any[] {
  if (!Array.isArray(blocks)) return [];
  return blocks.map((block) => transformBlock(block, ctx));
}

function transformBlock(block: any, ctx: MergeTagContext): any {
  if (!block || typeof block !== 'object') return block;
  const next = { ...block };
  if (Array.isArray(next.content)) {
    next.content = next.content.map((item: any) => {
      if (item && item.type === 'text' && typeof item.text === 'string') {
        const isTagOnly = /^\[[A-Za-z]+\.[A-Za-z]+\]$/.test(item.text.trim()) || /^\{\{\s*\w+\s*\}\}$/.test(item.text.trim());
        const resolvedText = resolveMergeTagsInText(item.text, ctx);
        
        let newStyles = item.styles;
        if (isTagOnly && newStyles) {
          // Strip the highlight colors we added for variables in the editor
          const { backgroundColor, textColor, ...restStyles } = newStyles;
          newStyles = restStyles;
        }

        return { ...item, text: resolvedText, styles: newStyles };
      }
      return item;
    });
  }
  if (Array.isArray(next.children)) {
    next.children = next.children.map((child: any) => transformBlock(child, ctx));
  }
  return next;
}
